import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TranslocoPipe } from '@jsverse/transloco';
import { ContextFacade } from '@core/context';
import { UserFacade } from 'src/app/store/user/user.facade';
import { PersonalInformationService } from '@services/api/internal/personal-information.service';
import { OrderingBlockedService } from '@services/utils/ordering-blocked.service';
import { EnvironmentService } from '@services/utils/environment.service';
import { BuildType } from '@shared/enums/build.type';
import { DataAttributeDirective } from '@shared/directives/data-attribute.directive';
import { createOrderingBlockedManagerSignal } from '@shared/utils/ordering-blocked-manager';
import { StartingPriceBannerDialogService } from './services/starting-price-banner-dialog.service';
import { DismissalRecord } from '@shared/utils/dismissal-schedule/dismissal-record.interface';
import {
  isDismissalActive,
  readStoredDismissal,
  registerDismissal,
  storeDismissal,
} from '@shared/utils/dismissal-schedule/dismissal-schedule';
import { getStartingPriceBannerStorageKey, STARTING_PRICE_BANNER_DISMISSAL_SCHEDULE } from './starting-price-banner-dismissal';

@Component({
  selector: 'tiss-starting-price-banner',
  imports: [ButtonModule, TranslocoPipe, DataAttributeDirective],
  templateUrl: './starting-price-banner.component.html',
  styleUrl: './starting-price-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StartingPriceBannerDialogService],
})
export class StartingPriceBannerComponent {
  private readonly contextFacade = inject(ContextFacade);
  private readonly userFacade = inject(UserFacade);
  private readonly personalInformationService = inject(PersonalInformationService);
  private readonly orderingBlockedService = inject(OrderingBlockedService);
  private readonly dialogService = inject(StartingPriceBannerDialogService);
  private readonly environmentService = inject(EnvironmentService);

  // Bumped on every close so `visible` re-reads the stored dismissal of the active contract.
  private readonly dismissalVersion = signal(0);
  // Fallback for browsers without storage access: dismissals made in this session.
  private readonly sessionDismissals = new Map<string, DismissalRecord>();
  private readonly legalOrganizationId = computed(() => {
    return this.contextFacade.$legalOrganization()?.id ?? this.userFacade.$user()?.defaultLegalOrganization?.id ?? null;
  });
  private readonly fallbackManager = computed(() => this.userFacade.$user()?.manager ?? null);
  private readonly fallbackWarehouse = computed(() => this.userFacade.$user()?.defaultWarehouse ?? null);

  protected readonly visible = computed(() => {
    const contractId = this.orderingBlockedService.activeContractId();
    this.dismissalVersion();

    return (
      this.environmentService.isBuildType(BuildType.ru) &&
      this.orderingBlockedService.selfRegistered() &&
      this.orderingBlockedService.orderingBlocked() &&
      contractId !== null &&
      !isDismissalActive(this.readDismissal(contractId), Date.now(), STARTING_PRICE_BANNER_DISMISSAL_SCHEDULE)
    );
  });
  protected readonly manager = createOrderingBlockedManagerSignal(
    this.visible,
    this.legalOrganizationId,
    this.personalInformationService,
    this.fallbackManager,
    this.fallbackWarehouse,
  );

  protected openManagerContacts(): void {
    this.dialogService.open(this.manager());
  }

  protected dismiss(): void {
    const contractId = this.orderingBlockedService.activeContractId();
    if (!contractId) {
      return;
    }

    const dismissal = registerDismissal(this.readDismissal(contractId), Date.now());
    // Keeps the dismissal effective for this session when browser storage is unavailable.
    this.sessionDismissals.set(contractId, dismissal);

    storeDismissal(getStartingPriceBannerStorageKey(contractId), dismissal);

    this.dismissalVersion.update(version => version + 1);
  }

  private readDismissal(contractId: string): DismissalRecord | null {
    const sessionDismissal = this.sessionDismissals.get(contractId);
    if (sessionDismissal) {
      return sessionDismissal;
    }

    return readStoredDismissal(getStartingPriceBannerStorageKey(contractId));
  }
}
